import type {
  AtualizarPedidoDto,
  AtualizarCompraDto,
  AtualizarRubricaDto,
  CalendarioResponse,
  CalendarioRecebimentoResponse,
  ComprasListaResponse,
  CopiarPrevistoDto,
  CriarCompraDto,
  CriarPedidoDto,
  CriarRubricaDto,
  CriarTokenIntegracaoDto,
  CompraMpResponse,
  FluxoQuery,
  FluxoResponse,
  GerarPedidoDeOrcamentoDto,
  ListarComprasQuery,
  ListarPedidosQuery,
  NecessidadeQuery,
  NecessidadeResponse,
  PedidoVendaResponse,
  PedidosListaResponse,
  PlanoParametrosDto,
  PlanoParametrosResponse,
  PrevistoMesResponse,
  RepetirPedidoDto,
  ResumoMesResponse,
  RubricaResponse,
  SalvarPrevistoDto,
  TokenIntegracaoCriadoResponse,
  TokenIntegracaoResponse,
} from '@triade/contracts';
import { useConsulta, useMutacao } from './base';

/*
 * Plano de vendas (Sessao 17). Dinheiro e taxa vem da API como string decimal; a tela so formata.
 * Chaves: ['carteira'] cobre lista, pedido, resumo e calendario; ['plano', ...] o resto.
 */

// ---------- Carteira (pedidos de venda) ----------

/** Pedidos (GET /carteira): `carteira.ver`. Filtros: mes, status, q, limit, offset. */
export function useCarteira(query: Partial<ListarPedidosQuery> = {}) {
  return useConsulta<PedidosListaResponse>(['carteira', 'lista'], '/carteira', { query });
}

/** Um pedido (GET /carteira/:id): `carteira.ver`. */
export function usePedido(id: string, ativo = true) {
  return useConsulta<PedidoVendaResponse>(['carteira', 'pedido', id], `/carteira/${id}`, { ativo: ativo && id.length > 0 });
}

/** Criacao (POST /carteira): `carteira.editar`. Invalida a carteira (lista, resumo, calendario) e o plano. */
export function useCriarPedido() {
  return useMutacao<PedidoVendaResponse, CriarPedidoDto>({
    metodo: 'POST',
    caminho: () => '/carteira',
    corpo: (v) => v,
    invalidar: [['carteira'], ['plano', 'necessidade'], ['plano', 'fluxo']],
  });
}

/** Alteracao (PATCH /carteira/:id): `carteira.editar`. */
export function useAtualizarPedido() {
  return useMutacao<PedidoVendaResponse, { id: string; dto: AtualizarPedidoDto }>({
    metodo: 'PATCH',
    caminho: (v) => `/carteira/${v.id}`,
    corpo: (v) => v.dto,
    invalidar: [['carteira'], ['plano', 'necessidade'], ['plano', 'fluxo']],
  });
}

/** Exclusao (DELETE /carteira/:id): `carteira.editar`. */
export function useExcluirPedido() {
  return useMutacao<void, { id: string }>({
    metodo: 'DELETE',
    caminho: (v) => `/carteira/${v.id}`,
    invalidar: [['carteira'], ['plano', 'necessidade'], ['plano', 'fluxo']],
  });
}

/** Repeticao em meses seguintes (POST /carteira/:id/repetir): `carteira.editar`. */
export function useRepetirPedido() {
  return useMutacao<PedidoVendaResponse[], { id: string; dto: RepetirPedidoDto }>({
    metodo: 'POST',
    caminho: (v) => `/carteira/${v.id}/repetir`,
    corpo: (v) => v.dto,
    invalidar: [['carteira'], ['plano', 'necessidade'], ['plano', 'fluxo']],
  });
}

/** Resumo do mes (GET /carteira/resumo?mes): `carteira.ver`. Faturamento/MC so com preco.ver/custo.ver. */
export function useResumoMes(mes: string) {
  return useConsulta<ResumoMesResponse>(['carteira', 'resumo', mes], '/carteira/resumo', { query: { mes } });
}

/** Calendario do mes por semana (GET /carteira/calendario?mes): `carteira.ver`. */
export function useCalendarioMes(mes: string) {
  return useConsulta<CalendarioResponse>(['carteira', 'calendario', mes], '/carteira/calendario', { query: { mes } });
}

/** Gera pedidos a partir de um orcamento aprovado (POST /carteira/de-orcamento/:id): `carteira.editar`. */
export function useGerarPedidoDeOrcamento() {
  return useMutacao<PedidoVendaResponse[], { id: string; dto: GerarPedidoDeOrcamentoDto }>({
    metodo: 'POST',
    caminho: (v) => `/carteira/de-orcamento/${v.id}`,
    corpo: (v) => v.dto,
    invalidar: [['carteira'], ['plano', 'necessidade'], ['plano', 'fluxo'], ['orcamentos']],
  });
}

// ---------- Parametros do plano ----------

/** Parametros (GET /plano/parametros): `plano.ver`. */
export function usePlanoParametros() {
  return useConsulta<PlanoParametrosResponse>(['plano', 'parametros'], '/plano/parametros');
}

/** Salvar parametros (PUT /plano/parametros): `plano.editar`. Muda frete net, pesos e aliquotas: invalida carteira e fluxo. */
export function useSalvarPlanoParametros() {
  return useMutacao<PlanoParametrosResponse, PlanoParametrosDto>({
    metodo: 'PUT',
    caminho: () => '/plano/parametros',
    corpo: (v) => v,
    invalidar: [['plano', 'parametros'], ['carteira'], ['plano', 'necessidade'], ['plano', 'fluxo']],
  });
}

// ---------- Rubricas e previsto ----------

/** Rubricas do custo fabrica (GET /plano/rubricas): `plano.ver`. */
export function useRubricas() {
  return useConsulta<RubricaResponse[]>(['plano', 'rubricas'], '/plano/rubricas');
}

/** Criacao de rubrica (POST /plano/rubricas): `plano.editar`. */
export function useCriarRubrica() {
  return useMutacao<RubricaResponse, CriarRubricaDto>({
    metodo: 'POST',
    caminho: () => '/plano/rubricas',
    corpo: (v) => v,
    invalidar: [['plano', 'rubricas'], ['plano', 'previsto'], ['carteira', 'resumo']],
  });
}

/** Alteracao de rubrica (PATCH /plano/rubricas/:id): `plano.editar`. */
export function useAtualizarRubrica() {
  return useMutacao<RubricaResponse, { id: string; dto: AtualizarRubricaDto }>({
    metodo: 'PATCH',
    caminho: (v) => `/plano/rubricas/${v.id}`,
    corpo: (v) => v.dto,
    invalidar: [['plano', 'rubricas'], ['plano', 'previsto'], ['carteira', 'resumo'], ['plano', 'fluxo']],
  });
}

/** Previsto do mes por rubrica (GET /plano/previsto/:mes): `plano.ver`. */
export function usePrevisto(mes: string) {
  return useConsulta<PrevistoMesResponse>(['plano', 'previsto', mes], `/plano/previsto/${mes}`);
}

/** Salvar previsto do mes (PUT /plano/previsto/:mes): `plano.editar`. Muda o custo fabrica e o resultado. */
export function useSalvarPrevisto(mes: string) {
  return useMutacao<PrevistoMesResponse, SalvarPrevistoDto>({
    metodo: 'PUT',
    caminho: () => `/plano/previsto/${mes}`,
    corpo: (v) => v,
    invalidar: [['plano', 'previsto'], ['carteira', 'resumo'], ['plano', 'fluxo']],
  });
}

/** Copia o previsto de outro mes (POST /plano/previsto/:mes/copiar): `plano.editar`. */
export function useCopiarPrevisto(mes: string) {
  return useMutacao<PrevistoMesResponse, CopiarPrevistoDto>({
    metodo: 'POST',
    caminho: () => `/plano/previsto/${mes}/copiar`,
    corpo: (v) => v,
    invalidar: [['plano', 'previsto'], ['carteira', 'resumo'], ['plano', 'fluxo']],
  });
}

// ---------- Compras de MP ----------

/** Compras de MP (GET /plano/compras): `plano.ver`. */
export function useCompras(query: Partial<ListarComprasQuery> = {}) {
  return useConsulta<ComprasListaResponse>(['plano', 'compras', 'lista'], '/plano/compras', { query });
}

/** Criacao de compra (POST /plano/compras): `plano.editar`. */
export function useCriarCompra() {
  return useMutacao<CompraMpResponse, CriarCompraDto>({
    metodo: 'POST',
    caminho: () => '/plano/compras',
    corpo: (v) => v,
    invalidar: [['plano', 'compras'], ['plano', 'necessidade'], ['plano', 'fluxo']],
  });
}

/** Alteracao de compra (PATCH /plano/compras/:id): `plano.editar`. */
export function useAtualizarCompra() {
  return useMutacao<CompraMpResponse, { id: string; dto: AtualizarCompraDto }>({
    metodo: 'PATCH',
    caminho: (v) => `/plano/compras/${v.id}`,
    corpo: (v) => v.dto,
    invalidar: [['plano', 'compras'], ['plano', 'necessidade'], ['plano', 'fluxo']],
  });
}

/** Exclusao de compra (DELETE /plano/compras/:id): `plano.editar`. */
export function useExcluirCompra() {
  return useMutacao<void, { id: string }>({
    metodo: 'DELETE',
    caminho: (v) => `/plano/compras/${v.id}`,
    invalidar: [['plano', 'compras'], ['plano', 'necessidade'], ['plano', 'fluxo']],
  });
}

/** Calendario de recebimento de MP (GET /plano/compras/calendario?mes): `plano.ver`. */
export function useCalendarioRecebimento(mes: string) {
  return useConsulta<CalendarioRecebimentoResponse>(['plano', 'compras', 'calendario', mes], '/plano/compras/calendario', { query: { mes } });
}

// ---------- Necessidade de MP e fluxo de caixa ----------

/** Necessidade de MP no periodo (GET /plano/necessidade): `plano.ver`. */
export function useNecessidade(query: NecessidadeQuery) {
  return useConsulta<NecessidadeResponse>(['plano', 'necessidade'], '/plano/necessidade', {
    query: { de: query.de, ate: query.ate, incluirPendente: query.incluirPendente === undefined ? undefined : String(query.incluirPendente) },
  });
}

/** Fluxo de caixa projetado (GET /plano/fluxo): `plano.ver`. */
export function useFluxo(query: FluxoQuery) {
  return useConsulta<FluxoResponse>(['plano', 'fluxo'], '/plano/fluxo', { query });
}

// ---------- Tokens de integracao ----------

/** Tokens da API do plano (GET /plano/tokens): `plano.editar`. Sem o valor do token. */
export function useTokensIntegracao() {
  return useConsulta<TokenIntegracaoResponse[]>(['plano', 'tokens'], '/plano/tokens');
}

/** Criacao de token (POST /plano/tokens): `plano.editar`. O valor do token vem so nesta resposta. */
export function useCriarTokenIntegracao() {
  return useMutacao<TokenIntegracaoCriadoResponse, CriarTokenIntegracaoDto>({
    metodo: 'POST',
    caminho: () => '/plano/tokens',
    corpo: (v) => v,
    invalidar: [['plano', 'tokens'], ['integracoes', 'resumo']],
  });
}

/** Revogacao de token (POST /plano/tokens/:id/revogar): `plano.editar`. */
export function useRevogarToken() {
  return useMutacao<TokenIntegracaoResponse, { id: string }>({
    metodo: 'POST',
    caminho: (v) => `/plano/tokens/${v.id}/revogar`,
    invalidar: [['plano', 'tokens'], ['integracoes', 'resumo']],
  });
}
